<template lang="pug">
.video-container
  .video-wrapper(
    v-if="!playInModal"
    v-show="isVideoPlaying || !placeholder"
    :class="'ratio-'+aspectRatio.replace(':','-')"
  )
    .video(:id="playerId" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;")
  .placeholder-container(v-if="(!isVideoPlaying || playInModal) && placeholder")
    DynamicMedia.placeholder(
      :config="placeholderConfig"
      @click="playVideo"
      lazy
    )
      .yt-placeholder-content(@click="onPlaceholderClick")
        slot
        slot(name="placeholder")
        button.play-icon.a11y(aria-label="Play" @click="playVideo")
          Icon(
            v-if="!hidePlayIcon"
            :name="placeholderIcon.name"
            :class="'icon-size-'+placeholderIcon.size"
            :fill="placeholderIcon.fill"
          )
  Modal(
    v-if="playInModal"
    v-show="modalVisible"
    width="1000px"
    :video="true"
    :disableScroll="false"
    @close="closeModal"
  )
    .video-wrapper(:class="'ratio-'+aspectRatio.replace(':','-')")
      .video.modal-iframe(:id="playerId" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;")
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, onServerPrefetch, ref, shallowRef, useId, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { allowScroll, preventScroll } from '../../../js/body-scroll'
import { useRogue } from '../../../js/rogue'
import { useRogueBuilder } from '../../../js/rogue-builder'
import { loadYouTubeAPI } from '../../../js/youtube-api'

/**
 * YouTubeVideo component can be used to inline videos on your page.
 * It requires two parameters: `placeholder', 'videoId` OR 'allVideoIds':
 * placeholder: Is a path to a placeholder image to display when video is not playing
 * videoId: Is the corresponding youtube video ID
 * allVideoIds: Is a string of videoIds within a playlist. It cannot contain spaces.
 */

interface PlayerVars {
  controls?: number,
  rel?: number,
  playsinline?: number,
  fs?: number,
  disablekb?: number,
  cc_lang_pref?: string,
  hl?: string,
  loop?: number,
  autoplay?: boolean,
  start?: number,
  end?: number,
  volume?: number,
  playlist?: string | null,
 }

interface PlayerIcon {
  name: string, // The name of the icon to display.
  size: string, // The size of the icon. Defaults to xxxxl. `xxs, xs, s, m, l, xl, xxl, xxxl, xxxxl`
  fill: string, // The fill color of the SVG icon. Can either use a design token or a raw color
}

interface Controls {
  playing?: boolean
  volume?: number
}

interface PlaceholderConfig {
  src?: string
  width?: number
  height?: number
  alt?: string
  url?: string
}

interface Props {
  placeholder?: Record<string, any> | string | null, // The image url used as placeholder
  videoId?: string, // The YouTube's video ID
  allVideoIds?: string[], // A list of all video IDs in a playlist
  playListIndex?: number, // The index in which the selected video belongs to. Used in conjunction with allVideoIds as a watcher
  playerVars?: Partial<PlayerVars> | null, // The YouTube player options https://developers.google.com/youtube/player_parameters.html?playerVersion=HTML5
  playerIcon?: Partial<PlayerIcon> | null, // Icon to show above the placeholder
  hidePlayIcon?: boolean,
  hideOnPause?: boolean,
  playInModal?: boolean,
  aspectRatio?: '16:9' | '21:9' | 'none',
  ldMeta?: Record<string, any>, // Optional - additional ldJSON props
  apiVideoName?: string | null,
  apiVideoDescription?: string | null,
  apiUploadDate?: string | null,
  autoplay?: boolean,
  controls?: Controls | null, // Allow controlling the player with a Controls object: {playing: boolean, volume: 0-100}
  nativeControls?: boolean, // Keep YouTube's own controls visible while still driven by `controls`
  start?: number,
  end?: number,
}

// YoutubeVideo type is defined in spa/server/api/video.ts
type ThumbnailSize = 'default' | 'medium' | 'high' | 'standard' | 'maxres'

type YoutubeVideo = {
  id: string,
  snippet: {
    description: string,
    thumbnails: {
      [key in ThumbnailSize]: {
        url: string,
        width: number,
        height: number,
      }
    },
    title: string,
    publishedAt: string,
  },
  statistics: {
    viewCount: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  placeholder         : null,
  playListIndex       : 0,
  playerVars          : null,
  playerIcon          : null,
  hidePlayIcon        : false,
  hideOnPause         : false,
  playInModal         : false,
  aspectRatio         : '16:9',
  apiVideoName        : null,
  apiVideoDescription : null,
  apiUploadDate       : null,
  autoplay            : false,
  controls            : () => ({}),
  nativeControls      : false,
})

const emit = defineEmits<{
  indexChanged: [index: number]
  unmute: []
}>()

const rogue = useRogue()
const isRogueBuilder = useRogueBuilder()
// The host app decides whether a play is worth tracking (e.g. competition pages)
const onVideoPlay = inject<(videoName?: string) => void>('onVideoPlay', () => {})

// shallowRef: the YouTube player is a large third-party object that shouldn't be deeply proxied
const player = shallowRef<any>(null)
const playerId = `youtube-player-${useId()}`
const videoName = ref<string | undefined>(undefined)
const videoDescription = ref<string | undefined>(undefined)
const uploadDate = ref<string | undefined>(undefined)
const thumbnail = ref<any>(undefined)
const readyToPlay = ref(false)
const isVideoPlaying = ref(false)
const modalVisible = ref(false)
let hasTrackedPlay = false

const playerVarsDefault: PlayerVars = {
  controls    : 1,
  rel         : 0,
  playsinline : 0,
  loop        : 0,
}

const playerIconDefault: PlayerIcon = {
  name : 'youtube-play',
  size : 'xxxxl',
  fill : 'rgba(255,255,255,0.75)',
}

const YOUTUBE_ERROR_MESSAGES: Record<number, string> = {
  2   : 'Invalid parameter value',
  5   : 'HTML5 player error',
  100 : 'Video not found',
  101 : 'Video not allowed to be played in embedded players',
  150 : 'Video not allowed to be played in embedded players',
}

const computedVideoName = computed(() => props.apiVideoName || videoName.value)
const computedVideoDescription = computed(() => props.apiVideoDescription || videoDescription.value)
const computedUploadDate = computed(() => props.apiUploadDate || uploadDate.value)

const placeholderConfig = computed<PlaceholderConfig | null>(() => {
  if (typeof props.placeholder === 'string' && props.aspectRatio !== 'none') {
    const [w, h] = props.aspectRatio.split(':')

    return {
      src    : props.placeholder,
      width  : parseInt(w, 10) * 100,
      height : parseInt(h, 10) * 100,
      alt    : 'Play Video',
    }
  }

  return props.placeholder as PlaceholderConfig | null
})

const computedVideoThumbnail = computed(() => placeholderConfig.value?.src || thumbnail.value?.url)

const computedVideoId = computed(() => props.allVideoIds ? props.allVideoIds[props.playListIndex] : props.videoId)

const ldJSON = computed(() => {
  return {
    '@context'   : 'https://schema.org',
    '@type'      : 'VideoObject',
    name         : computedVideoName.value,
    description  : computedVideoDescription.value,
    uploadDate   : computedUploadDate.value,
    thumbnailUrl : computedVideoThumbnail.value,
    contentUrl   : `https://www.youtube.com/watch?v=${computedVideoId.value}`,
    embedUrl     : `https://www.youtube.com/embed/${computedVideoId.value}`,
    ...(props.ldMeta || {}),
  }
})

const placeholderIcon = computed(() => ({
  ...playerIconDefault,
  ...props.playerIcon,
}))

const ytPlayerVars = computed<PlayerVars>(() => {
  const vars: PlayerVars = {
    ...playerVarsDefault,
    // Match the player UI and preferred captions to the site's language (ISO 639-1, e.g. "fr")
    ...(rogue?.l10n?.lang ? { hl: rogue.l10n.lang, cc_lang_pref: rogue.l10n.lang } : {}),
    ...props.playerVars,
    start    : props.start,
    end      : props.end,
    autoplay : props.autoplay || props.playerVars?.autoplay,
  }

  if (props.controls?.playing !== undefined) {
    vars.controls = props.nativeControls ? 1 : 0
    vars.playsinline = 1
    if (props.controls.volume !== undefined) {
      vars.volume = props.controls.volume
    }
  }

  return vars
})

const rendersLdJson = Boolean(props.videoId && !isRogueBuilder)

if (rendersLdJson) {
  useHead({
    script: computed(() => [
      {
        type      : 'application/ld+json',
        key       : `video-${props.videoId}-ld-json`,
        innerHTML : JSON.stringify(ldJSON.value),
      },
    ]),
  })
}

// `controls` can be null (MMV passes null when shared state is off), so read it optionally
watch(() => props.controls?.playing, (newVal) => {
  if (!player.value) {
    return
  }
  try {
    if (newVal && !isVideoPlaying.value) {
      player.value.playVideo()
      isVideoPlaying.value = true
    } else if (!newVal && isVideoPlaying.value) {
      player.value.stopVideo()
      isVideoPlaying.value = false
    }
  } catch (e) {
    console.warn(`Error ${newVal ? 'playing' : 'stopping'} youtube`, e)
  }
})

watch(() => props.controls?.volume, (volume) => {
  if (volume !== undefined && player.value?.setVolume) {
    applyVolume(volume)
  }
})

// Browsers only allow autoplay for a truly muted player; setVolume(0) alone still gets blocked
function applyVolume(volume: number) {
  player.value.setVolume(volume)
  if (volume === 0) {
    player.value.mute()
  } else if (player.value.isMuted?.()) {
    player.value.unMute()
  }
}

// The iframe API has no volume event, so poll to mirror native volume/mute changes into `controls`
let nativeVolumeInterval: ReturnType<typeof setInterval> | null = null
let lastPlayerVolume: number | null = null

function pollNativeVolume() {
  if (!player.value?.getVolume || !props.controls) {
    return
  }
  const volume = player.value.isMuted() ? 0 : Math.round(player.value.getVolume())
  // Compare against the player's last reading so lagging reads after setVolume aren't mistaken for user input
  if (lastPlayerVolume !== null && volume !== lastPlayerVolume && volume !== props.controls.volume) {
    props.controls.volume = volume
    if (volume > 0) {
      emit('unmute')
    }
  }
  lastPlayerVolume = volume
}

function stopNativeVolumePolling() {
  if (nativeVolumeInterval) {
    clearInterval(nativeVolumeInterval)
    nativeVolumeInterval = null
  }
  lastPlayerVolume = null
}

watch(() => props.nativeControls && readyToPlay.value, (polling) => {
  stopNativeVolumePolling()
  if (polling) {
    nativeVolumeInterval = setInterval(pollNativeVolume, 500)
  }
})

watch(() => props.playListIndex, (value) => {
  if (player.value && player.value.getPlaylistIndex() !== value) {
    player.value.playVideoAt(value)
  }
})

async function initializePlayer() {
  await loadYouTubeAPI()
  createPlayer()
}

function createPlayer() {
  const playerOptions: Record<string, any> = {
    playerVars: {
      ...ytPlayerVars.value,
      playlist : props.allVideoIds ? props.allVideoIds.toString() : null,
      origin   : window.location.origin,
    },
    videoId : computedVideoId.value,
    events  : {
      onReady           : onPlayerReady,
      onStateChange     : onStateChange,
      onError           : onPlayerError,
      onAutoplayBlocked : onAutoplayBlocked,
    },
  }

  player.value = new window.YT!.Player(playerId, playerOptions)
}

function destroyPlayer() {
  if (player.value && typeof player.value.destroy === 'function') {
    player.value.destroy()
    player.value = null
  }
}

function onPlayerReady() {
  readyToPlay.value = true

  if (props.controls?.playing) {
    // Mute first so a muted split-screen tile is allowed to autoplay
    const volume = props.controls?.volume
    if (volume !== undefined) {
      applyVolume(volume)
    }

    player.value.playVideo()
  }
}

function setPlaying(playing: boolean) {
  isVideoPlaying.value = playing
  if (props.controls) {
    props.controls.playing = playing
  }
}

function onStateChange(e: any) {
  if (props.allVideoIds && e.target?.getPlaylistIndex) {
    const index = e.target.getPlaylistIndex()

    if (index !== props.playListIndex) {
      emit('indexChanged', index)
    }
  }

  const { PlayerState } = window.YT!
  switch (e.data) {
    case PlayerState.PLAYING:
      if (!hasTrackedPlay) {
        hasTrackedPlay = true
        onVideoPlay(computedVideoName.value)
      }
      setPlaying(true)
      break
    case PlayerState.PAUSED:
    case PlayerState.ENDED:
    case PlayerState.UNSTARTED:
    case PlayerState.CUED:
      setPlaying(false)
      break
    // BUFFERING keeps the current state so seeking doesn't flash the placeholder
  }
}

function onPlayerError(e: { data?: number; target?: unknown }) {
  const errorCode = e?.data
  const errorMessage = YOUTUBE_ERROR_MESSAGES[errorCode!] ?? `YouTube player error (code: ${errorCode ?? 'unknown'})`

  rogue?.log?.error('YouTube player error', new Error(errorMessage), {
    errorCode,
    videoId: computedVideoId.value,
    playerId,
  })
}

function onAutoplayBlocked(e: any) {
  if (props.controls?.playing) {
    props.controls.playing = false
  }
  rogue?.log?.warn('YouTube player autoplay was blocked by the browser', e)
}

function playVideo() {
  if (readyToPlay.value) {
    if (props.playInModal) {
      modalVisible.value = true
      preventScroll()
    }
    const volume = props.controls?.volume
    if (volume !== undefined) {
      applyVolume(volume)
    }
    player.value.playVideo()
  }
}

function closeModal() {
  player.value.pauseVideo()
  allowScroll()
  modalVisible.value = false
  isVideoPlaying.value = false
}

// Lets CMS content inside the placeholder act as a play button via a `#play` link
function onPlaceholderClick(event: any) {
  const link = ((event.target.closest('button') || {}).dataset || {}).link || (event.target.closest('a') || {}).href
  if (link === '#play') {
    event.preventDefault()
    event.stopPropagation()
    playVideo()
  }
}

function getCurrentTime() {
  if (player.value) {
    return player.value.getCurrentTime()
  }

  return undefined
}

function pauseVideo() {
  if (player.value) {
    player.value.pauseVideo()
  }
}

async function getVideoData() {
  if (!props.videoId) {
    return
  }
  // populate additional video data fields for the video ld+json
  try {
    const additionalVideoData = await rogue.fetch<YoutubeVideo[]>(`/svc_ssr/video-detail/?videoId=${props.videoId}`)
    // api returns an array with one object.
    // snippet holds all of the information that we want
    if (!additionalVideoData?.[0]?.snippet) {
      return
    }
    const snippet = additionalVideoData[0].snippet

    videoName.value = snippet.title
    videoDescription.value = snippet.description
    uploadDate.value = snippet.publishedAt
    thumbnail.value = snippet.thumbnails.standard

  } catch (ex) {
    rogue.log?.warn('Failed to fetch additional video details.')
  }
}

onBeforeUnmount(() => {
  if (modalVisible.value) {
    allowScroll()
  }

  stopNativeVolumePolling()
  destroyPlayer()
})

// The fetched details only feed the ld+json, so skip the request when nothing will use it
const needsVideoData = rendersLdJson && !(props.apiVideoName && props.apiVideoDescription && props.apiUploadDate)

onServerPrefetch(async () => {
  if (needsVideoData) {
    await getVideoData()
  }
})

if (!rogue.ssr && needsVideoData) {
  getVideoData()
}

onMounted(() => {
  initializePlayer()
})

defineExpose({
  getCurrentTime,
  pauseVideo,
})
</script>

<style lang="scss" scoped>
.video-container {
  display: block;
}

.video-wrapper {
  position: relative;
  height: 700px;

  &.ratio {
    &-none {
      overflow: hidden;
      max-width: 100%;
    }

    &-16-9 {
      height: 0;
      padding-bottom: 56.25%;
      overflow: hidden;
      max-width: 100%;
    }

    &-21-9 {
      height: 0;
      padding-bottom: 42.85%;
      overflow: hidden;
      max-width: 100%;
    }
  }

  .modal-iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.placeholder-container {
  position: relative;
  cursor: pointer;

  .placeholder {
    max-width: 100%;
    margin: auto;
    display: block;
    cursor: pointer;
  }

  .yt-placeholder-content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
}
</style>
